import React from 'react';
import {
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  isReuseAllowed,
  ServiceSelection,
} from '../hooks/useDeployServiceSelection';
import { useServiceVersions } from '../hooks/useServiceVersions';

export interface ServiceModeRowProps {
  projectId: string | null;
  /** Canonical appname (matches config.SiteBatchOrder). */
  appname: string;
  /** Optional display label ("Router", "Lights"). Falls back to appname. */
  label?: string;
  /** Target site — used to block reuse on infra sites. */
  site: string;
  /** Current selection for this service. */
  value: ServiceSelection;
  /** Fires when the operator changes mode or picks a version. */
  onChange: (next: ServiceSelection) => void;
  /**
   * 현장 통합서버 배포 — 현장에서는 빌드하지 않으므로 빌드 칸을 숨기고, reuse 는
   * "사내 이미지 승격" 이라 "승격" 으로 부른다.
   */
  remote?: boolean;
}

/**
 * One row of the deploy-service-mode-select table: `[서비스] [라디오] [버전 드롭다운]`.
 *
 * Behavior rules (matches design §5.3):
 * - Build mode: version cell shows "—"
 * - Reuse mode: version Select is shown + required; loading state while
 *   `useServiceVersions` fetches
 * - Reuse disabled when (a) site is infra, (b) appname is reuse-incompatible
 *   (e.g. config-only), or (c) GitHub API call failed — tooltip explains why
 * - Versions are fetched lazily: query is only `enabled` when the row is in
 *   reuse mode, so switching to build unblocks the budget for other rows
 */
export default function ServiceModeRow({
  projectId,
  appname,
  label,
  site,
  value,
  onChange,
  remote = false,
}: ServiceModeRowProps) {
  const displayLabel = label ?? appname;
  const reuseDisabledByPolicy = !isReuseAllowed(site, appname);
  const isReuse = value.mode === 'reuse';

  const versionsQuery = useServiceVersions(projectId, appname, {
    enabled: isReuse && !reuseDisabledByPolicy,
    source: remote ? 'company' : 'git',
  });

  const apiFailed = versionsQuery.isError;
  const apiLoading = versionsQuery.isLoading && isReuse;
  // 미리 채운 버전(원격: 사내에 떠 있는 버전)이 태그 목록에 없어도 선택값으로 보이게.
  const listed = versionsQuery.data ?? [];
  const versions =
    value.target_version && !listed.some((v) => v.name === value.target_version)
      ? [{ name: value.target_version }, ...listed]
      : listed;

  const reuseDisabled = reuseDisabledByPolicy || apiFailed;
  const reuseDisabledReason = reuseDisabledByPolicy
    ? reuseDisabledByPolicy && site === 'infra'
      ? 'infra 사이트는 재사용을 지원하지 않습니다'
      : '이 서비스는 항상 빌드됩니다'
    : apiFailed
      ? '버전 목록을 불러올 수 없습니다'
      : '';

  const handleModeChange = (_: React.ChangeEvent<HTMLInputElement>, next: string) => {
    if (next === 'reuse' && reuseDisabled) return;
    if (next === 'build') {
      onChange({ mode: 'build' });
    } else if (next === 'skip') {
      // deploy-service-skip: switching to skip drops the target_version
      // to keep the state deterministic when the row flips back to reuse.
      onChange({ mode: 'skip' });
    } else {
      onChange({ mode: 'reuse', target_version: value.target_version });
    }
  };

  const handleVersionChange = (next: string) => {
    onChange({ mode: 'reuse', target_version: next });
  };

  return (
    <TableRow data-testid={`service-mode-row-${appname}`}>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {displayLabel}
        </Typography>
        {displayLabel !== appname && (
          <Typography variant="caption" color="text.secondary">
            {appname}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <RadioGroup row value={value.mode} onChange={handleModeChange}>
          {!remote && (
            <FormControlLabel value="build" control={<Radio size="small" />} label="빌드" />
          )}
          <Tooltip
            title={reuseDisabled ? reuseDisabledReason : ''}
            disableHoverListener={!reuseDisabled}
          >
            {/* span wrapper — Tooltip requires non-disabled child to trigger */}
            <span>
              <FormControlLabel
                value="reuse"
                disabled={reuseDisabled}
                control={<Radio size="small" />}
                label={remote ? '승격' : '재사용'}
              />
            </span>
          </Tooltip>
          <FormControlLabel
            value="skip"
            control={<Radio size="small" />}
            label="배포안함"
          />
        </RadioGroup>
      </TableCell>
      <TableCell>
        {isReuse && !reuseDisabled ? (
          <Select
            size="small"
            value={value.target_version ?? ''}
            displayEmpty
            onChange={(e) => handleVersionChange(String(e.target.value))}
            disabled={apiLoading}
            sx={{ minWidth: 140 }}
            inputProps={{ 'aria-label': `${appname} version` }}
          >
            <MenuItem value="" disabled>
              {apiLoading ? '로딩중…' : '버전 선택'}
            </MenuItem>
            {versions.map((v) => (
              <MenuItem key={v.name} value={v.name}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}
